import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format, isBefore } from "date-fns";
import { useNavigate } from "react-router-dom";

import { HourglassEmpty, Search } from "@mui/icons-material";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import { alpha } from "@mui/material/styles";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  InputAdornment,
  FormControl,
  InputLabel,
  OutlinedInput,
  Icon,
  Chip,
  IconButton,
  Typography,
  Pagination,
  Alert,
  Button,
  Tooltip,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Stack,
} from "@mui/material";

import Progress from "components/Progress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { LojaPageContainer } from "layouts/pages/loja/common";

import {
  EventosProps,
  getAllEventos,
  getAllEventosPeridoByActive,
  getEventosByStatus,
  getEventosEmProcessamentoResumo,
  getSeachFilterEventos,
} from "services/evento";

import { ICountEvento, IEventoResponse } from "interfaces/eventos";
import { getcolorBadge } from "helpers/colorBadge";

import logoTnv from "assets/images/favicon.png";
import { useDebouncedValue } from "hooks/useDebouncedValue";

const SEARCH_DEBOUNCE_MS = 500;
const BANNER_DISMISS_KEY = "eventos-banner-processamento-dismissed";
const ACCENT = "#F96030";

type ViewMode = "abertos" | "processamento" | "todos";

function getPendenciasPublicacao(count?: ICountEvento): string[] {
  if (!count) return [];
  const faltando: string[] = [];
  if (!count.lotesEventos) faltando.push("lotes");
  if (!count.distanciaEvento) faltando.push("distâncias/categorias");
  if (!count.formaPaymentoEvento) faltando.push("tipo de pagamento");
  return faltando;
}

function getListagemSubtitle(viewMode: ViewMode): string {
  switch (viewMode) {
    case "processamento":
      return "Cadastros em andamento";
    case "todos":
      return "Todos os eventos cadastrados";
    default:
      return "Eventos com inscrições abertas";
  }
}

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography variant="h4" fontWeight="bold" color={accent ? ACCENT : "text.primary"}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

interface Props {
  stickyNavbar?: boolean;
}

export default function Eventos(_props: Props): JSX.Element {
  const [eventos, setEventos] = useState<EventosProps>({
    eventos: [],
    total: 0,
    currentPage: 0,
    totalPages: 0,
  });
  const [query, setQuery] = useState<string>("");
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const isSearch = debouncedQuery.trim().length > 0;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [viewMode, setViewMode] = useState<ViewMode>("abertos");
  const [isloading, setIsloading] = useState<boolean>(true);
  const [emProcessamentoCount, setEmProcessamentoCount] = useState(0);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem(BANNER_DISMISS_KEY) === "1",
  );
  const prevDebouncedRef = useRef<string | null>(null);
  const navigate = useNavigate();

  const loadResumo = useCallback(async () => {
    try {
      const { emProcessamento } = await getEventosEmProcessamentoResumo();
      setEmProcessamentoCount(emProcessamento);
    } catch {
      setEmProcessamentoCount(0);
    }
  }, []);

  useEffect(() => {
    loadResumo();
  }, [loadResumo]);

  useEffect(() => {
    if (prevDebouncedRef.current === null) {
      prevDebouncedRef.current = debouncedQuery;
      return;
    }
    if (prevDebouncedRef.current === debouncedQuery) return;
    prevDebouncedRef.current = debouncedQuery;
    setCurrentPage(1);
  }, [debouncedQuery]);

  useEffect(() => {
    (async () => {
      setIsloading(true);

      try {
        let resp: EventosProps;

        if (isSearch) {
          resp = await getSeachFilterEventos(currentPage, 10, debouncedQuery.trim());
        } else if (viewMode === "processamento") {
          resp = await getEventosByStatus(currentPage, 10);
        } else if (viewMode === "todos") {
          resp = await getAllEventos(currentPage, 10, "all");
        } else {
          resp = await getAllEventosPeridoByActive(currentPage, 10);
        }

        setEventos(resp);
        await loadResumo();
      } finally {
        setIsloading(false);
      }
    })();
  }, [currentPage, viewMode, isSearch, debouncedQuery, loadResumo]);

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, next: ViewMode | null) => {
    if (!next) return;
    setCurrentPage(1);
    setViewMode(next);
  };

  const handleVerEmProcessamento = () => {
    setCurrentPage(1);
    setQuery("");
    setViewMode("processamento");
  };

  const handleDismissBanner = () => {
    sessionStorage.setItem(BANNER_DISMISS_KEY, "1");
    setBannerDismissed(true);
  };

  const showProcessamentoBanner =
    emProcessamentoCount > 0 &&
    !bannerDismissed &&
    viewMode !== "processamento" &&
    !isSearch;

  const hrefEvento = "/layouts/pages/cadastros/eventos";
  const handleNavigateNewEvento = () => navigate(`${hrefEvento}/criar`);
  const handleNavigateEditEvento = (slug: string) => navigate(`${hrefEvento}/${slug}`);

  const isEventoEncerrado = (data: Date | string) =>
    isBefore(data.toString().slice(0, 10).concat("T23:59:59"), new Date());

  const stats = useMemo(() => {
    const emProcPagina = eventos.eventos.filter(
      (e) => e.status?.tipo === "Processamento",
    ).length;
    return {
      total: eventos.total,
      emProcessamento: emProcessamentoCount,
      emProcPagina,
    };
  }, [eventos.eventos, eventos.total, emProcessamentoCount]);

  const columns: GridColDef<IEventoResponse>[] = [
    {
      field: "id",
      headerName: "ID",
      width: 60,
      type: "number",
      renderCell(params) {
        return (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleNavigateEditEvento(params.row.slug);
            }}
          >
            <Icon>edit</Icon>
          </IconButton>
        );
      },
    },
    {
      field: "urlImageDestaque",
      headerName: "Card",
      width: 90,
      renderCell(params) {
        return (
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={72}
            height={40}
            sx={{ flexShrink: 0 }}
          >
            <Box
              component="img"
              src={params.row.urlImageDestaque}
              onError={(e) => (e.currentTarget.src = logoTnv)}
              alt=""
              sx={{
                width: 72,
                height: 40,
                objectFit: "contain",
                objectPosition: "center",
                borderRadius: "5px",
                display: "block",
              }}
            />
          </MDBox>
        );
      },
    },
    {
      field: "titulo",
      headerName: "Evento",
      width: 300,
    },
    {
      field: "dataEvento",
      headerAlign: "center",
      headerName: "Data Evento",
      align: "center",
      width: 180,
      renderCell(params) {
        return (
          <MDBox display="flex" flexDirection="column" alignItems="center">
            <p>
              {format(
                params.row.dataEvento.slice(0, 10).concat("T23:59:59.000Z"),
                "dd/MM/yyyy",
              )}
            </p>
          </MDBox>
        );
      },
    },
    {
      field: "modalidade",
      headerName: "Modalidade",
      width: 200,
      renderCell(params) {
        return <>{params.row.modalidade.tipo}</>;
      },
    },
    {
      field: "status",
      headerAlign: "center",
      headerName: "Status",
      width: 150,
      align: "center",
      renderCell(params) {
        const isProcessamento = params.row.status.tipo === "Processamento";
        return (
          <Chip
            label={params.row.status.tipo}
            variant="outlined"
            color={getcolorBadge(params.row.status.tipo)}
            icon={isProcessamento ? <HourglassEmpty fontSize="small" /> : undefined}
          />
        );
      },
    },
    {
      field: "progresso",
      headerName: "Progresso",
      width: 200,
      sortable: false,
      renderCell(params) {
        if (isEventoEncerrado(params.row.dataEvento)) {
          return (
            <Typography variant="caption" fontWeight="bold" color="error">
              Encerrado
            </Typography>
          );
        }

        if (params.row.status.tipo !== "Processamento") return null;

        const pendencias = getPendenciasPublicacao(params.row._count);
        if (pendencias.length === 0) {
          return (
            <Typography variant="caption" color="success.main" fontWeight="bold">
              Pronto para publicar
            </Typography>
          );
        }

        const texto = `Falta: ${pendencias.join(", ")}`;
        return (
          <Tooltip title={texto}>
            <Typography
              variant="caption"
              color="warning.main"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {texto}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "limiteInscricao",
      headerName: "Limite",
      width: 150,
      renderCell(params) {
        const totalInscritos = params.row._count?.inscritos || 0;
        const totalCancelados = params.row._count?.inscritos_cancelado || 0;
        const totalInscEfetivo = totalInscritos - totalCancelados;
        return (
          <Progress inscritos={totalInscEfetivo} limite={params.row.limiteInscricao} />
        );
      },
    },
  ];

  const emptyMessage =
    viewMode === "processamento"
      ? "Nenhum evento em processamento"
      : isSearch
        ? "Nenhum evento encontrado para esta busca"
        : "Nenhum evento nesta visualização";

  return (
    <LojaPageContainer
      title="Eventos"
      subtitle={getListagemSubtitle(viewMode)}
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          onClick={handleNavigateNewEvento}
          sx={{
            flexShrink: 0,
            whiteSpace: "nowrap",
            background: `linear-gradient(195deg, ${ACCENT}, #e04a1a)`,
          }}
        >
          <Icon sx={{ fontWeight: "bold", mr: 0.5 }}>add</Icon>
          Novo evento
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4}>
          <StatMini
            label={isSearch ? "Resultados da busca" : "Total na listagem"}
            value={stats.total}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatMini label="Em processamento" value={stats.emProcessamento} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatMini label="Rascunhos (página)" value={stats.emProcPagina} />
        </Grid>
      </Grid>

      <MDBox
        mb={2}
        p={2}
        borderRadius="lg"
        sx={{
          bgcolor: "grey.50",
          border: "1px solid",
          borderColor: "grey.200",
          borderLeft: `4px solid ${ACCENT}`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <EventAvailableOutlinedIcon sx={{ color: ACCENT, flexShrink: 0 }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              {isSearch ? `Busca: “${debouncedQuery.trim()}”` : getListagemSubtitle(viewMode)}
            </MDTypography>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ flexShrink: 0 }}
          >
            <FormControl variant="outlined" size="small" sx={{ minWidth: { sm: 260 } }}>
              <InputLabel htmlFor="outlined-search">Pesquisar evento</InputLabel>
              <OutlinedInput
                id="outlined-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <Search fontSize="small" />
                  </InputAdornment>
                }
                label="Pesquisar evento"
                sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
              />
            </FormControl>

            {!isSearch && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    px: 1.5,
                    py: 0.75,
                    textTransform: "none",
                    fontSize: "0.8125rem",
                    whiteSpace: "nowrap",
                    "&.Mui-selected": {
                      bgcolor: "rgba(249, 96, 48, 0.1)",
                      borderColor: ACCENT,
                      color: ACCENT,
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <ToggleButton value="abertos">Abertos</ToggleButton>
                {emProcessamentoCount > 0 && (
                  <ToggleButton value="processamento">
                    <PendingActionsOutlinedIcon sx={{ fontSize: "1rem !important", mr: 0.5 }} />
                    Processamento ({emProcessamentoCount})
                  </ToggleButton>
                )}
                <ToggleButton value="todos">Todos</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Stack>
        </Stack>
      </MDBox>

      {showProcessamentoBanner && (
        <MDBox mb={2}>
          <Alert
            severity="info"
            sx={{ borderRadius: 2 }}
            action={
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button color="inherit" size="small" onClick={handleVerEmProcessamento}>
                  Ver em processamento
                </Button>
                <Button color="inherit" size="small" onClick={handleDismissBanner}>
                  Dispensar
                </Button>
              </Box>
            }
          >
            Você tem {emProcessamentoCount} evento
            {emProcessamentoCount === 1 ? "" : "s"} com cadastro em andamento. Conclua lotes,
            distâncias e pagamento para publicar.
          </Alert>
        </MDBox>
      )}

      {!isloading && eventos.eventos.length === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <EventAvailableOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            {emptyMessage}
          </MDTypography>
          <MDTypography variant="body2" color="text" mb={2}>
            {isSearch
              ? "Tente outro termo ou limpe a busca."
              : viewMode === "abertos"
                ? "Crie um evento ou verifique a aba Todos."
                : "Altere o filtro ou cadastre um novo evento."}
          </MDTypography>
          {!isSearch && (
            <MDButton
              variant="gradient"
              color="dark"
              onClick={handleNavigateNewEvento}
              sx={{
                background: `linear-gradient(195deg, ${ACCENT}, #e04a1a)`,
              }}
            >
              Criar evento
            </MDButton>
          )}
        </MDBox>
      ) : (
        <>
          <DataGrid
            sx={{
              border: "1px solid #eee",
              borderRadius: 1,
              "& .MuiDataGrid-columnHeaders": { backgroundColor: "#fafafa" },
              "& .MuiDataGrid-row": { cursor: "pointer" },
              "& .evento-em-processamento": {
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
              },
            }}
            loading={isloading}
            columns={columns}
            rowHeight={68}
            rows={eventos.eventos}
            onRowClick={(params) => handleNavigateEditEvento(params.row.slug)}
            getRowClassName={(params) =>
              params.row.status?.tipo === "Processamento" ? "evento-em-processamento" : ""
            }
            hideFooter
            hideFooterPagination
            autoHeight
            localeText={{ noRowsLabel: emptyMessage }}
            disableRowSelectionOnClick
          />

          <MDBox
            display="flex"
            flexDirection={{ xs: "column-reverse", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            mt={2}
            gap={1}
          >
            <Typography variant="body2" color="text.secondary">
              Página {eventos.currentPage} de {eventos.totalPages || 1} · {eventos.total}{" "}
              {eventos.total === 1 ? "evento" : "eventos"}
            </Typography>
            <Pagination
              count={eventos.totalPages}
              page={eventos.currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              showFirstButton
              showLastButton
              color="primary"
              size="small"
            />
          </MDBox>
        </>
      )}
    </LojaPageContainer>
  );
}
